import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { DropdownWithCheckboxesItem } from "@/components/DropdownWithCheckboxes/DropdownWithCheckboxes";

import { useGetCompanyUsersQuery } from "@/pages/Private/redux/admin/admin.api";

import { CompanyUser } from "@/pages/Private/pages/Users/schema/get-users";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useAppSelector } from "@/redux/hooks";

const ALL_ITEM: DropdownWithCheckboxesItem = { id: "all", title: "All" };

interface ComposerUserLike {
	id?: number;
	firstName?: string | null;
	lastName?: string | null;
}

const buildUserTitle = (composerUser: ComposerUserLike, currentUserId?: number): string => {
	const name = `${composerUser.firstName ?? ""} ${composerUser.lastName ?? ""}`.trim();

	return composerUser.id === currentUserId ? `Me (${name})` : name;
};

export const buildComposerOptions = (
	companyUsers: CompanyUser[],
	currentUserId?: number
): DropdownWithCheckboxesItem[] => {
	return [
		ALL_ITEM,
		...companyUsers.map((companyUser) => ({
			id: companyUser.id,
			title: buildUserTitle(companyUser, currentUserId),
		})),
	];
};

export const getComposersFromUrl = (
	search: string,
	currentUser?: ComposerUserLike
): DropdownWithCheckboxesItem[] => {
	const param = new URLSearchParams(search).get("composers");

	if (param) {
		try {
			return JSON.parse(param) as DropdownWithCheckboxesItem[];
		} catch {
			// Malformed URL state — fall back to the default selection below
		}
	}

	return currentUser?.id
		? [{ id: currentUser.id, title: buildUserTitle(currentUser, currentUser.id) }]
		: [ALL_ITEM];
};

export const toComposerIds = (selected: DropdownWithCheckboxesItem[]): number[] | undefined => {
	// "All" means no filter at all — records without a composer (created before
	// this feature existed) must also be included then.
	if (selected.some((item) => item.id === "all")) {
		return undefined;
	}

	const ids = selected.map((item) => item.id).filter((id): id is number => typeof id === "number");

	return ids.length > 0 ? ids : undefined;
};

interface UseComposerFilterResult {
	composerOptions: DropdownWithCheckboxesItem[];
	selectedComposers: DropdownWithCheckboxesItem[];
	composerIds: number[] | undefined;
	handleComposerSelect: (values: DropdownWithCheckboxesItem[]) => void;
}

/**
 * Composer filter for the Compose page: which Signalist users' compose
 * prospects (list_person.createdByUserId) are shown. Lists ALL company users —
 * not just sending users — because a composer may send via another user.
 * Defaults to "Me"; the selection is persisted in the `composers` URL param
 * (same format as the Inbox `senders` param) so the sidebar badge can follow it.
 */
export const useComposerFilter = (): UseComposerFilterResult => {
	const navigate = useNavigate();
	const { user } = useAppSelector(authSelector);
	const { data: companyUsers } = useGetCompanyUsersQuery();

	const [selectedComposers, setSelectedComposers] = useState<DropdownWithCheckboxesItem[]>(() =>
		getComposersFromUrl(location.search, user ?? undefined)
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		params.set("composers", JSON.stringify(selectedComposers));

		navigate({ search: params.toString() }, { replace: true });
	}, [selectedComposers, navigate]);

	// On query error the options degrade to just "All" + "Me" so the filter
	// keeps working for users who cannot list their colleagues.
	const composerOptions = useMemo(() => {
		const users: CompanyUser[] =
			companyUsers?.data && companyUsers.data.length > 0
				? companyUsers.data
				: user
					? [{ id: user.id, firstName: user.firstName, lastName: user.lastName }]
					: [];

		return buildComposerOptions(users, user?.id);
	}, [companyUsers, user]);

	const handleComposerSelect = (values: DropdownWithCheckboxesItem[]): void => {
		const addedValue = values.find((val) => !selectedComposers.map((s) => s.id).includes(val.id));

		const newValues = values.filter((val) => val.id !== "all");

		const removedValue = selectedComposers.find((val) => !values.map((s) => s.id).includes(val.id));

		if (addedValue && addedValue.id === "all") {
			setSelectedComposers(composerOptions);
		} else if (removedValue && removedValue.id === "all") {
			setSelectedComposers(getComposersFromUrl("", user ?? undefined));
		} else {
			setSelectedComposers(newValues);
		}
	};

	const composerIds = useMemo(() => toComposerIds(selectedComposers), [selectedComposers]);

	return { composerOptions, selectedComposers, composerIds, handleComposerSelect };
};
